import { createSelector } from 'reselect';
import { ShippoApi } from '../util/api';
import { StatusCode } from '../util/enums';
import { storableError } from '../util/errors';
import { showErrorToast } from '../util/toast';
import { fetchCurrentUser } from './user.duck';

// ================ Action types ================ //

export const ShippoActionTypes = Object.freeze({
  CREATE_ADDRESS_REQUEST: 'app/shippo/CREATE_SHIPPO_ADDRESS_REQUEST',
  CREATE_ADDRESS_SUCCESS: 'app/shippo/CREATE_SHIPPO_ADDRESS_SUCCESS',
  CREATE_ADDRESS_ERROR: 'app/shippo/CREATE_SHIPPO_ADDRESS_ERROR',

  FETCH_CARRIER_REQUEST: 'app/shippo/FETCH_CARRIER_REQUEST',
  FETCH_CARRIER_SUCCESS: 'app/shippo/FETCH_CARRIER_SUCCESS',
  FETCH_CARRIER_ERROR: 'app/shippo/FETCH_CARRIER_ERROR',

  // ✅ NEW: Tracking
  FETCH_TRACKING_REQUEST: 'app/shippo/FETCH_TRACKING_REQUEST',
  FETCH_TRACKING_SUCCESS: 'app/shippo/FETCH_TRACKING_SUCCESS',
  FETCH_TRACKING_ERROR: 'app/shippo/FETCH_TRACKING_ERROR',
});

// ================ Reducer ================ //

const initialState = {
  saveShippoAddressInProgress: false,
  shippoAddressSaved: false,
  saveShippoAddressError: null,
  clearForm: false,
  saveWeightInProgress: false,
  fetchListingsInProgress: false,
  listingsFetched: false,
  allListings: [],
  fetchCarriersInProgress: false,
  carriersData: {},
  carriersError: null,
  isInternational: null,

  // ✅ NEW: Tracking state
  fetchTrackingInProgress: false,
  trackingData: null,
  trackingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case ShippoActionTypes.CREATE_ADDRESS_REQUEST:
      return { ...state, saveShippoAddressInProgress: true, saveShippoAddressError: null };

    case ShippoActionTypes.CREATE_ADDRESS_SUCCESS:
      return {
        ...state,
        saveShippoAddressInProgress: false,
        shippoAddressSaved: true,
        saveShippoAddressError: null,
      };

    case ShippoActionTypes.CREATE_ADDRESS_ERROR:
      return { ...state, saveShippoAddressInProgress: false, saveShippoAddressError: true };

    case ShippoActionTypes.FETCH_CARRIER_REQUEST:
      return { ...state, fetchCarriersInProgress: true, carriersError: null };

    case ShippoActionTypes.FETCH_CARRIER_SUCCESS:
      return { ...state, fetchCarriersInProgress: false, carriersData: payload };

    case ShippoActionTypes.FETCH_CARRIER_ERROR:
      return { ...state, fetchCarriersInProgress: false, carriersError: payload };

    // ✅ NEW: Tracking reducer cases
    case ShippoActionTypes.FETCH_TRACKING_REQUEST:
      return {
        ...state,
        fetchTrackingInProgress: true,
        trackingError: null,
      };

    case ShippoActionTypes.FETCH_TRACKING_SUCCESS:
      return {
        ...state,
        fetchTrackingInProgress: false,
        trackingData: payload,
      };

    case ShippoActionTypes.FETCH_TRACKING_ERROR:
      return {
        ...state,
        fetchTrackingInProgress: false,
        trackingError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const createShippoAddressRequest = () => ({
  type: ShippoActionTypes.CREATE_ADDRESS_REQUEST,
});
export const createShippoAddressSuccess = () => ({
  type: ShippoActionTypes.CREATE_ADDRESS_SUCCESS,
});
export const createShippoAddressError = () => ({ type: ShippoActionTypes.CREATE_ADDRESS_ERROR });

export const fetchCarriersRequest = () => ({ type: ShippoActionTypes.FETCH_CARRIER_REQUEST });
export const fetchCarriersSuccess = data => ({
  type: ShippoActionTypes.FETCH_CARRIER_SUCCESS,
  payload: data,
});
export const fetchCarriersError = error => ({
  type: ShippoActionTypes.FETCH_CARRIER_ERROR,
  payload: error,
});

export const fetchTrackingRequest = () => ({ type: ShippoActionTypes.FETCH_TRACKING_REQUEST });
export const fetchTrackingSuccess = data => ({
  type: ShippoActionTypes.FETCH_TRACKING_SUCCESS,
  payload: data,
});
export const fetchTrackingError = error => ({
  type: ShippoActionTypes.FETCH_TRACKING_ERROR,
  payload: error,
});

// ================ Selectors (memoized) ================ //

const shippoState = state => state.shippo;

export const selectShippoAddressStatus = createSelector(
  [shippoState],
  shippo => {
    const { saveShippoAddressInProgress, saveShippoAddressError, shippoAddressSaved } = shippo;
    return {
      saveShippoAddressInProgress,
      saveShippoAddressError,
      shippoAddressSaved,
    };
  }
);

export const fetchShippingCarriersSelector = createSelector(
  [shippoState],
  shippo => {
    const { carriersData, carriersError, fetchCarriersInProgress } = shippo;
    return {
      carriersData,
      fetchCarriersError: carriersError,
      fetchCarriersInProgress,
    };
  }
);

// ✅ NEW: Tracking selector
export const fetchShippoTrackingSelector = createSelector(
  [shippoState],
  shippo => {
    const { trackingData, trackingError, fetchTrackingInProgress } = shippo;
    return {
      trackingData,
      trackingError,
      fetchTrackingInProgress,
    };
  }
);

// ================ Thunks ================ //

export const createShippoAddress = params => async (dispatch, getState, sdk) => {
  if (!params || !Object.keys(params).length) return;

  dispatch(createShippoAddressRequest());

  try {
    const response = await ShippoApi.createAddress(params);

    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchCurrentUser());
      dispatch(createShippoAddressSuccess());
      return response;
    }

    dispatch(createShippoAddressError());
    return response;
  } catch (error) {
    dispatch(createShippoAddressError());
    showErrorToast('Failed to save shipping address');
  }
};

export const fetchShippingCarriers = params => async (dispatch, getState, sdk) => {
  dispatch(fetchCarriersRequest());

  try {
    const response = await ShippoApi.getCarriers(params);

    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchCarriersSuccess(response.data));
    } else {
      dispatch(fetchCarriersError(new Error('Failed to fetch carriers')));
    }

    return response;
  } catch (error) {
    dispatch(fetchCarriersError(storableError(error)));
  }
};

export const fetchShippoTracking = params => async dispatch => {
  const trackingNumber = params?.trackingNumber || params?.tracking_number;

  if (!trackingNumber) {
    const err = new Error('trackingNumber is required');
    dispatch(fetchTrackingError(storableError(err)));
    return;
  }

  dispatch(fetchTrackingRequest());

  try {
    const response = await ShippoApi.getTrackingByNumber(params);

    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchTrackingSuccess(response.data));
      return response;
    }

    const err = new Error(response?.message || 'Failed to fetch tracking details');
    dispatch(fetchTrackingError(storableError(err)));
    return response;
  } catch (error) {
    dispatch(fetchTrackingError(storableError(error)));
  }
};

export const loadData = () => fetchCurrentUser();
